import React, { useCallback, useEffect } from 'react';
import accounting from 'accounting';
import { List, Typography } from 'antd';
import {
  calculateNetAmount,
  calculateTaxValues,
  calculateTotalAmount,
  EstimateItemForCalculation,
} from '../../lib/modelHelpers/estimate';

import styles from './CalculationList.module.less';

const { Text, Title } = Typography;

export type EstimateCalculationListProps = {
  estimateItems: readonly EstimateItemForCalculation[];
};

function EstimateCalculationList(props: EstimateCalculationListProps) {
  const [dataSource, setDataSource] = React.useState<
    { type: string; title: string; amount: string }[]
  >([]);

  const updateInvoiceItemsState = useCallback(
    (estimateItems: readonly EstimateItemForCalculation[]) => {
      const netAmount = calculateNetAmount(estimateItems);
      const taxValues = calculateTaxValues(estimateItems);
      const totalAmount = calculateTotalAmount(estimateItems);

      const newDataSource: any[] = [];
      newDataSource.push({
        type: 'net',
        title: 'Nettobetrag',
        amount: accounting.formatMoney(netAmount / 100, '', 2, '.', ',') + ' €',
      });
      for (const taxValue of taxValues) {
        if (taxValues.length === 1 || (taxValues.length > 1 && taxValue.taxRate !== 0)) {
          newDataSource.push({
            type: 'tax',
            title: `Umsatzsteuer ${taxValue.taxRate} %`,
            amount: taxValue.taxAmount + ' €',
          });
        }
      }
      newDataSource.push({
        type: 'total',
        title: 'Gesamtbetrag',
        amount: accounting.formatMoney(totalAmount / 100, '', 2, '.', ',') + ' €',
      });
      setDataSource(newDataSource);
    },
    []
  );

  useEffect(() => {
    updateInvoiceItemsState(props.estimateItems);
  }, [props.estimateItems, updateInvoiceItemsState]);

  return (
    <List
      className={styles.list}
      dataSource={dataSource}
      renderItem={(item) => (
        <List.Item className={item.type === 'total' ? styles.totalRow : styles.row}>
          <div className={styles.label}>
            {item.type === 'total' ? (
              <Title level={4} className={styles.totalLabel}>
                {item.title}
              </Title>
            ) : (
              <Text className={styles.text}>{item.title}</Text>
            )}
          </div>
          <div className={styles.value}>
            {item.type === 'total' ? (
              <Title level={4} className={styles.totalValue}>
                {item.amount}
              </Title>
            ) : (
              <Text className={styles.text}>{item.amount}</Text>
            )}
          </div>
        </List.Item>
      )}
    />
  );
}

export default React.memo(EstimateCalculationList, function areEqual(prevProps, nextProps) {
  return JSON.stringify(prevProps) === JSON.stringify(nextProps);
});
